import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { hasAnyPermission, hasModuleAccess, hasPermission, type AuthUser } from './permissions';

export interface PermissionRouteProps {
  children?: React.ReactNode;
  permission?: string;
  permissions?: string[];
  module?: string;
  getAuthToken: () => string | null;
  fetchUser: () => Promise<AuthUser>;
  loginPath?: string;
  noAccessPath?: string;
}

const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  permission,
  permissions = [],
  module,
  getAuthToken,
  fetchUser,
  loginPath,
  noAccessPath = '/panel/no-access',
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const resolvedLogin =
      loginPath ??
      (`${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/login`.replace(/\/+/g, '/') || '/login');

    const token = getAuthToken();
    if (!token) {
      window.location.replace(resolvedLogin.startsWith('/') ? resolvedLogin : `/${resolvedLogin}`);
      return;
    }

    fetchUser()
      .then((loadedUser) => {
        setUser(loadedUser);

        const requiredPermissions = permission ? [permission, ...permissions] : permissions;
        let isAllowed = true;

        if (module && !hasModuleAccess(loadedUser, module)) {
          isAllowed = false;
        }

        if (requiredPermissions.length > 0 && !hasAnyPermission(loadedUser, requiredPermissions)) {
          isAllowed = false;
        }

        if (!isAllowed) {
          window.location.replace(noAccessPath);
          return;
        }

        setAllowed(true);
      })
      .catch(() => {
        window.location.replace(resolvedLogin.startsWith('/') ? resolvedLogin : `/${resolvedLogin}`);
      })
      .finally(() => setLoading(false));
  }, [permission, permissions, module, getAuthToken, fetchUser, loginPath, noAccessPath]);

  if (loading || allowed !== true) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-sky-600" />
      </div>
    );
  }

  return children ? <>{children}</> : <Outlet context={{ user }} />;
};

export { hasPermission, hasModuleAccess, hasAnyPermission };
export default PermissionRoute;
